const BASE = '/s/art/around_the_home';

const NAV_LINKS = [
  { key: 'nature', href: `${BASE}?pub_home_art_subject=has_any%3Anature`, title: 'Nature & Landscape' },
  { key: 'abstract', href: `${BASE}?pub_home_art_subject=has_any%3Aabstract`, title: 'Contemporary & Abstract' },
  { key: 'emotive', href: `${BASE}?pub_home_art_subject=has_any%3Aemotive`, title: 'Personal & Emotive' },
];

const FAQ_ITEMS = [
  {
    question: 'What is Jewish art for the home?',
    answer: [
      'Jewish art for the home is wall art chosen for everyday Jewish spaces, such as hallways, bedrooms, offices, playrooms, and kitchens. It includes nature and landscape paintings, pomegranate and olive tree art, contemporary and abstract pieces, and works rooted in Jewish identity and belonging. It is distinct from landmark art, Gedolim portraits, and Jewish life scenes, though some pieces naturally overlap.',
    ],
  },
  {
    question: 'What types of art are available in this collection?',
    answer: [
      'The collection includes three main types. Nature and landscape pieces cover florals, landscapes, pomegranate and olive tree paintings, and Shivat Haminim — some rooted in Eretz Yisrael, some more universal in subject. Contemporary and abstract pieces cover colour-led, textured, and expressive works that suit a wide range of interiors, not all of which are specifically Jewish in subject. Jewish identity and peoplehood pieces cover works rooted in Am Yisrael, Ahava, unity, and Jewish belonging — symbolic rather than devotional or scene-based.',
    ],
  },
  {
    question: 'What is pomegranate art and why is it a popular Jewish home piece?',
    answer: [
      'The pomegranate is one of the Seven Species of Israel mentioned in the Torah and carries deep Jewish symbolism — abundance, blessing, and the fullness of Jewish life. Pomegranate paintings and prints are among the most common Jewish home art choices because the subject is recognisably Jewish, visually warm, and works in almost any room. They are particularly popular for housewarmings, entryways, and kitchens, and make a natural Rosh Hashanah or new home gift.',
    ],
  },
  {
    question: 'Which rooms in the home suit this type of art?',
    answer: [
      'Nature and landscape pieces — pomegranates, olive trees, florals, landscapes — tend to suit hallways, entryways, kitchens, and children\'s rooms. They are warm and accessible without demanding attention. Contemporary and abstract pieces are more placement-flexible and work well in offices, playrooms, guest bedrooms, and teen rooms where you want visual presence without a specific subject. Personal and emotive pieces are chosen by feeling rather than room — intimate, atmospheric, or identity-driven works where meaning matters more than placement.',
    ],
  },
  {
    question: 'Is this type of art a good gift?',
    answer: [
      'Yes. Pomegranate paintings and landscape pieces make strong housewarming gifts because they are warm, meaningful, and work across a wide range of tastes and spaces. Abstract and contemporary pieces suit buyers who know the recipient\'s style and interior well. Jewish identity pieces — Ahava, Am Yisrael Chai, Acheinu — work for milestone occasions and recipients with a strong personal connection to the Jewish people or Eretz Yisrael.',
    ],
  },
  {
    question: 'Can I commission a specific piece for a particular room or occasion?',
    answer: [
      'Yes. If you have a specific subject, colour palette, size, or room in mind, you can message an artist directly through their profile or submit a request through the <a href="/artist/custom_co_studio">commissions hub</a>. Nature subjects, abstract pieces, and identity themes are all available as commissioned work.',
    ],
  },
];

const getSchema = rootURL => {
  const pageURL = `${rootURL}/s/art/around_the_home`;
  return [
    {
      '@type': 'CollectionPage',
      '@id': `${pageURL}#collectionpage`,
      url: pageURL,
      name: 'Jewish Art for the Home',
      description:
        'Shop Jewish art for the home by independent Jewish artists, including Israeli landscapes, pomegranate paintings, contemporary Judaic wall art, abstract pieces, and Jewish identity art.',
      isPartOf: { '@id': `${rootURL}/#website` },
      about: [
        { '@type': 'Thing', name: 'Jewish home art' },
        { '@type': 'Thing', name: 'Jewish wall art' },
        { '@type': 'Thing', name: 'Israeli landscape painting' },
        { '@type': 'Thing', name: 'pomegranate art' },
        { '@type': 'Thing', name: 'Jewish nature art' },
        { '@type': 'Thing', name: 'contemporary Jewish art' },
        { '@type': 'Thing', name: 'abstract Jewish art' },
        { '@type': 'Thing', name: 'Jewish identity art' },
        { '@type': 'Thing', name: 'Shivat Haminim art' },
        { '@type': 'Thing', name: 'Jewish art for hallways' },
        { '@type': 'Thing', name: 'Judaic wall art' },
      ],
      publisher: { '@id': `${rootURL}/#organization` },
      mainEntity: {
        '@type': 'ItemList',
        name: 'Jewish art for the home collection',
        itemListOrder: 'https://schema.org/ItemListUnordered',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Nature & Landscape',
            description:
              'Landscapes, florals, pomegranate and olive tree paintings, Shivat Haminim, and nature works for hallways, entryways, and everyday spaces.',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Contemporary & Abstract',
            description:
              'Colour-led, textured, and expressive works for offices, playrooms, and flexible everyday spaces.',
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: 'Personal & Emotive',
            description:
              'Pieces chosen for feeling rather than subject — intimate, atmospheric, and emotionally resonant works including Jewish identity and belonging pieces, soft personal art, and anything chosen for meaning over placement.',
          },
        ],
      },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${pageURL}#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${rootURL}` },
        { '@type': 'ListItem', position: 2, name: 'Art', item: `${rootURL}/s/art` },
        { '@type': 'ListItem', position: 3, name: 'Jewish Art for the Home', item: pageURL },
      ],
    },
    {
      '@type': 'FAQPage',
      '@id': `${pageURL}#faq`,
      mainEntity: FAQ_ITEMS.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer[0].replace(/<[^>]*>/g, ''),
        },
      })),
    },
  ];
};

const aroundTheHomeSeoConfig = {
  key: 'around_the_home',
  match: ({ listingType, activeCollection }) =>
    listingType === 'art' && activeCollection === 'around_the_home',
  accentColor: '--colorArt',
  links: NAV_LINKS,
  title: 'Jewish Art for the Home | Modern, Nature & Judaic Wall Art | Joodaic',
  description:
    'Jewish art for hallways, bedrooms, playrooms, and everyday spaces — landscapes, pomegranate paintings, abstract art, and Jewish identity pieces by independent Jewish artists.',
  ogImage: {
    url: 'https://sharetribe.imgix.net/67c4adcd-cbbe-48c1-a752-e48bba4bb050/67e425ad-7c67-42b2-b461-00f46c7c7da1?auto=format&fit=clip&h=1024&w=1024&s=bfdeb828d4d6dc6bdd97f0e8ea652845',
    width: '1024',
    height: '1024',
  },
  getSchema,
  intro: {
    heading: 'Jewish Art for the Home',
    tagline:
      'Jewish art for everyday spaces: hallways, bedrooms, playrooms, offices, and the walls that need something that simply belongs.',
    collapsedHeading: 'Jewish Art for the Home',
    bodyParagraphs: [
      'Not every Jewish artwork is a landmark, a portrait, or a scene. Some pieces are chosen because they fit the space, or because they mean something personal without needing to anchor the room.',
      'This collection brings together art for the walls that rarely get written about: nature and landscape paintings, pomegranate and olive tree pieces, contemporary and abstract works, and art rooted in Jewish identity, Am Yisrael, and belonging. Some pieces are clearly Jewish or Israeli in subject. Others are quieter, chosen less for what they depict and more for how naturally they sit in a Jewish home.',
    ],
    linksLabel: 'Browse by subject',
  },
  bottomSections: [
    {
      id: 'positioning',
      type: 'prose',
      heading: 'Jewish art for every room',
      paragraphs: [
        'Most Jewish art is bought for the living room or the dining room — the rooms that need a statement. This collection is for the rest of the home: the hallway that gets walked a hundred times a day, the bedroom that needs something calming and Jewish, the playroom or children\'s room that should feel warm without being heavy, the office or study that needs presence without drama.',
        'Prices range across the collection, from fine art prints to original paintings and mixed-media pieces. Most listings are available framed or unframed, in multiple sizes.',
        'Nature and landscape pieces make strong housewarming gifts — pomegranate paintings in particular travel well across tastes and spaces. Jewish identity pieces work well for milestone occasions where personal meaning matters more than room placement.',
      ],
      paragraphsAfter: [
        'For Jerusalem and Kotel scenes, browse <a href="/s/art/the_classics">Jerusalem &amp; Kotel art</a>.',
        'For portraits of gedolim, rebbes, and Torah leaders, browse <a href="/s/art/gedolim">Gedolim portraits and paintings</a>.',
        'For scenes of Jewish life, tefillah, Shabbos, and celebration, browse <a href="/s/art/jewish_icons">Jewish Life &amp; Tradition Art</a>.',
        'Looking for a specific subject, room, or style? You can <a href="/artist/custom_co_studio">commission a custom piece</a> from a Joodaic artist.',
      ],
    },
    {
      id: 'nature',
      title: 'Nature & Landscape',
      paragraphs: [
        'Landscapes, florals, nature scenes, pomegranate and olive tree paintings, ocean, mountains, Shivat Haminim, and the natural world. Some pieces are rooted in Eretz Yisrael — the Judean Hills, the Galil, the Seven Species of Israel. Others are simply beautiful nature and landscape works that feel at home in a Jewish space without a specifically Jewish subject.',
        'These pieces tend to suit the spaces that get walked past every day: hallways, entryways, kitchens, and children\'s rooms. Pomegranate art is a particularly common choice for entryways and kitchens — the symbolism is warm and familiar, and the subject travels well across interior styles. Landscape and floral pieces work well in hallways and bedrooms where you want something grounding and calm without a narrative.',
      ],
    },
    {
      id: 'abstract',
      title: 'Contemporary & Abstract',
      paragraphs: [
        'Contemporary and abstract pieces built around colour, texture, and movement. Some carry subtle Jewish references in their palette or subject. Many do not — they are chosen because they are visually strong and feel right in the home of a Jewish family.',
        'These are the most placement-flexible pieces in the collection. They suit offices, playrooms, guest bedrooms, teen rooms, and hallways where you want visual energy without a specific subject. Neutral and earth-toned pieces work in modern interiors. More colour-led works suit playrooms and rooms where you want warmth and life on the wall.',
      ],
    },
    {
      id: 'emotive',
      title: 'Personal & Emotive',
      paragraphs: [
        'Pieces chosen for feeling rather than subject. Some are rooted in Jewish identity and belonging — Am Yisrael Chai, Ahava, Acheinu, Beyachad Nenatzeiach, Brotherly Love — and carry meaning through symbol, word, and emotional resonance. Others are quieter and more intimate: atmospheric, dreamy, or personal works that resist easy categorisation but feel right in a private space.',
        'These pieces are not chosen primarily by room or aesthetic fit. They are chosen because of what they evoke — a connection to the Jewish people, a feeling of warmth and closeness, or simply a piece that carries a meaning the buyer wants nearby. Where they hang tends to be secondary to why they were chosen.',
      ],
    },
  ],
  faqHeading: 'FAQ',
  faq: FAQ_ITEMS,
};

export default aroundTheHomeSeoConfig;
